import type { ReactNode } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FeatureFaq from '@/components/marketing/features/FeatureFaq'
import ModuleDetailCard from './ModuleDetailCard'
import TechBreadcrumbs from './TechBreadcrumbs'
import TechCta from './TechCta'
import TechSubnav from './TechSubnav'
import type { TechnologySubPageProps } from '@/types/technology'

/* The shared shell for the REFERENCE pages — Distribution, Release QC and
 * Creative & audience (audience-first plan, 2026-10-18).
 *
 * They differ only in the props TechnologyController builds and in `visual`,
 * so they are thin page components over this one layout. Each still needs its
 * own .tsx under pages/marketing/technology/ — that path is what the marketing
 * entrypoint's glob resolves `render inertia: "technology/Platform"` against.
 *
 * The owner's problem was "scrambled; not easy to read or navigate", so the
 * modules no longer run as one flat list: they sit under their page's 3–5
 * REFERENCE_GROUPS headings, each an h2 whose id is the group key (the hub's
 * capability groups link to those anchors). A short jump-list — one link per
 * group, not one per module — replaces the old chip row, and there is no
 * status legend: a card is badged only when its module is NOT live, so the
 * only badges a reader meets are the ones that are a warning.
 *
 * The closing essays are gone; the answer leads and the FAQ closes, visible
 * text and never folded.
 */
export default function TechnologySubPage({
  hero,
  answer,
  faq,
  breadcrumbs,
  groups,
  subnav,
  brand,
  cta,
  visual,
}: TechnologySubPageProps & { visual?: ReactNode }) {
  const shown = groups.filter((group) => group.modules.length > 0)

  return (
    <>
      <AtmosphericHero minHeight="46vh">
        <div className="mx-auto max-w-3xl text-center">
          <TechBreadcrumbs crumbs={breadcrumbs} />

          <ScrollReveal>
            <p className="marketing-eyebrow-mono justify-center">{hero.eyebrow}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.05}>
            <p className="marketing-eyebrow mt-4 justify-center">
              {hero.section}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="hero-headline mt-5" style={{ fontSize: 'clamp(2.1rem, 4.6vw, 3.6rem)' }}>
              {hero.title}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <p className="marketing-lead mx-auto mt-7 max-w-2xl">{hero.body}</p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      <TechSubnav items={subnav} brand={brand} />

      {answer && (
        <section className="container mx-auto px-6 pt-14 lg:pt-16">
          <p className="marketing-lead mx-auto max-w-3xl">{answer}</p>
        </section>
      )}

      {/* One link per group, never per module: the old 23-chip row was itself
          part of the clutter. */}
      {shown.length > 1 && (
        <nav aria-label="Sections on this page" className="container mx-auto px-6 pt-10">
          <ul className="mx-auto flex max-w-5xl flex-wrap gap-2">
            {shown.map((group) => (
              <li key={group.key}>
                <a
                  href={`#${group.key}`}
                  className="marketing-chip inline-flex items-center gap-2 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)]"
                >
                  {group.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {visual && (
        <section className="container mx-auto px-6 pt-14 lg:pt-16">
          <ScrollReveal>
            <div className="mx-auto max-w-5xl">{visual}</div>
          </ScrollReveal>
        </section>
      )}

      <div className="container mx-auto px-6 py-16 lg:py-20">
        <div className="mx-auto flex max-w-5xl flex-col gap-16">
          {shown.map((group) => (
            <section key={group.key} aria-labelledby={`${group.key}-heading`}>
              <div id={group.key} className="scroll-mt-52 sm:scroll-mt-44 border-b border-white/[0.07] pb-4">
                <h2
                  id={`${group.key}-heading`}
                  className="text-2xl font-semibold text-white sm:text-[1.75rem]"
                  style={{ fontFamily: 'var(--wavbee-font-display)', letterSpacing: '-0.02em' }}
                >
                  {group.title}
                </h2>
                <p className="mt-2 max-w-[65ch] text-[0.95rem] leading-[1.7] text-white/60">{group.line}</p>
              </div>
              <div className="mt-6 flex flex-col gap-5">
                {group.modules.map((module, index) => (
                  <ModuleDetailCard key={module.key} module={module} index={index} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {faq && faq.length > 0 && <FeatureFaq faq={faq} />}

      <TechCta cta={cta} />
    </>
  )
}
